import { useState, useEffect, useRef, useCallback } from "react";
import API from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Textarea } from "./ui/textarea";
import { FileDropZone } from "./FileDropZone";
import { openAuthFile } from "../lib/fileOpen";
import { Trash, PencilSimple, File as FileIcon, Note, At } from "@phosphor-icons/react";
import { toast } from "sonner";

const MENTION_RE = /@([\w.-]+)/g;

function renderWithMentions(text) {
  const parts = [];
  let lastIdx = 0;
  let m;
  MENTION_RE.lastIndex = 0;
  while ((m = MENTION_RE.exec(text)) !== null) {
    if (m.index > lastIdx) parts.push(text.slice(lastIdx, m.index));
    parts.push(
      <span key={`mention-${m.index}`} className="inline-flex items-center rounded px-1 py-0.5 text-[11px] font-semibold bg-amber-200/60 text-amber-900 dark:bg-amber-500/20 dark:text-amber-300">
        @{m[1]}
      </span>
    );
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < text.length) parts.push(text.slice(lastIdx));
  return parts;
}

/* Shared entity notes (jobs / emergency jobs / projects / requests / quotes).
   Author-only editing with an "edited" badge. @mentions notify teammates. */
export default function NotesSection({ entityType, entityId, notes = [], onChanged, folder = "notes", title = "Notes" }) {
  const { user } = useAuth();
  const uid = String(user?._id || "");
  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");

  const [mentionable, setMentionable] = useState([]);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionStart, setMentionStart] = useState(-1);
  const [mentionIdx, setMentionIdx] = useState(0);
  const textareaRef = useRef(null);

  const fetchMentionable = useCallback(async () => {
    try {
      const { data } = await API.get("/private-notes/mentionable-users");
      setMentionable(data.users || []);
    } catch { /* silent */ }
  }, []);
  useEffect(() => { fetchMentionable(); }, [fetchMentionable]);

  const onChangeText = (e) => {
    const val = e.target.value;
    setText(val);
    const caret = e.target.selectionStart ?? val.length;
    const upto = val.slice(0, caret);
    const match = upto.match(/(^|\s)@([\w.-]*)$/);
    if (match) {
      setMentionOpen(true);
      setMentionQuery(match[2].toLowerCase());
      setMentionStart(caret - match[2].length - 1);
      setMentionIdx(0);
    } else {
      setMentionOpen(false);
    }
  };

  const filtered = mentionable
    .filter(u => {
      if (!mentionQuery) return true;
      const q = mentionQuery;
      return (
        (u.name || "").toLowerCase().includes(q) ||
        (u.handle || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().split("@")[0].includes(q)
      );
    })
    .slice(0, 6);

  const insertMention = (u) => {
    if (mentionStart < 0) return;
    const handle = u.handle || (u.name || "").replace(/\s+/g, ".");
    const before = text.slice(0, mentionStart);
    const after = text.slice((textareaRef.current?.selectionStart) ?? text.length);
    setText(`${before}@${handle} ${after}`);
    setMentionOpen(false);
    setTimeout(() => {
      textareaRef.current?.focus();
      const pos = (before + `@${handle} `).length;
      textareaRef.current?.setSelectionRange(pos, pos);
    }, 0);
  };

  const handleKeyDown = (e) => {
    if (!mentionOpen || filtered.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setMentionIdx(i => (i + 1) % filtered.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setMentionIdx(i => (i - 1 + filtered.length) % filtered.length); }
    else if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); insertMention(filtered[mentionIdx]); }
    else if (e.key === "Escape") { setMentionOpen(false); }
  };

  const add = async () => {
    if (!text.trim()) return;
    setAdding(true);
    try {
      const { data } = await API.post(`/notes/${entityType}/${entityId}`, { text: text.trim(), attachments });
      const mentionedCount = (data.mentioned_users || []).length;
      toast.success(mentionedCount > 0 ? `Note added — ${mentionedCount} colleague${mentionedCount > 1 ? "s" : ""} notified` : "Note added");
      setText("");
      setAttachments([]);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to add note"); }
    finally { setAdding(false); }
  };

  const saveEdit = async () => {
    if (!editText.trim()) return;
    try {
      await API.put(`/notes/${entityType}/${entityId}/${editingId}`, { text: editText.trim() });
      toast.success("Note updated");
      setEditingId(null);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to update note"); }
  };

  const del = async (noteId) => {
    try {
      await API.delete(`/notes/${entityType}/${entityId}/${noteId}`);
      toast.success("Note deleted");
      onChanged?.();
    } catch { toast.error("Failed to delete note"); }
  };

  return (
    <div data-testid={`notes-section-${entityType}`}>
      <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2.5 flex items-center gap-1">
        <Note size={12} /> {title} ({notes.length})
      </h3>
      <div className="mb-3 border border-border rounded-md p-3 relative">
        <Textarea ref={textareaRef} value={text} onChange={onChangeText} onKeyDown={handleKeyDown} rows={2} placeholder="Add a note... Type @ to mention a teammate (they'll be notified)" className="mb-2" data-testid={`${entityType}-note-input`} />
        {mentionOpen && filtered.length > 0 && (
          <div className="absolute left-3 right-3 z-20 rounded-md border border-border bg-popover shadow-lg overflow-hidden max-h-64 overflow-y-auto" data-testid={`${entityType}-mention-dropdown`}>
            {filtered.map((u, i) => (
              <button
                key={u.id}
                type="button"
                onClick={() => insertMention(u)}
                onMouseEnter={() => setMentionIdx(i)}
                className={`w-full text-left flex items-center gap-2 px-2.5 py-2 text-xs transition-colors ${i === mentionIdx ? "bg-accent" : "hover:bg-accent/60"}`}
                data-testid={`${entityType}-mention-opt-${u.id}`}
              >
                <At size={12} className="text-amber-600 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="font-semibold truncate">{u.name}</p>
                  <p className="text-[10px] text-muted-foreground truncate">{u.email}</p>
                </div>
                <span className="text-[9px] uppercase tracking-wider text-muted-foreground font-medium shrink-0">{(u.role || "").replace("_", " ")}</span>
              </button>
            ))}
          </div>
        )}
        <FileDropZone files={attachments} onFilesChange={setAttachments} folder={folder} accept="all" maxFiles={5} compact className="mb-2" />
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-muted-foreground opacity-70 flex items-center gap-1"><At size={10} /> Type @ to mention</span>
          <Button size="sm" onClick={add} disabled={adding || !text.trim()} data-testid={`${entityType}-add-note-btn`}>
            {adding ? "Adding..." : "Add Note"}
          </Button>
        </div>
      </div>
      {notes.length === 0 ? (
        <p className="text-center text-sm text-muted-foreground py-6">No notes yet</p>
      ) : (
        <div className="space-y-3">
          {[...notes].reverse().map((n) => (
            <div key={n.id} className="border border-border rounded-md p-3" data-testid={`${entityType}-note-${n.id}`}>
              <div className="flex items-start justify-between mb-1">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold">{n.user_name}</span>
                  <Badge variant="outline" className="text-[9px]">{n.user_role?.replace("_", " ")}</Badge>
                  {n.edited && <Badge variant="outline" className="text-[9px] text-muted-foreground" data-testid={`${entityType}-note-edited-${n.id}`}>edited</Badge>}
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-[10px] text-muted-foreground">{new Date(n.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                  {n.user_id === uid && (
                    <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => { setEditingId(n.id); setEditText(n.text); }} data-testid={`${entityType}-note-edit-${n.id}`}>
                      <PencilSimple size={10} />
                    </Button>
                  )}
                  <Button variant="ghost" size="icon" className="h-5 w-5 text-destructive" onClick={() => del(n.id)} data-testid={`${entityType}-note-delete-${n.id}`}>
                    <Trash size={10} />
                  </Button>
                </div>
              </div>
              {editingId === n.id ? (
                <div>
                  <Textarea value={editText} onChange={(e) => setEditText(e.target.value)} rows={2} className="mb-2" data-testid={`${entityType}-note-edit-input`} />
                  <div className="flex gap-2 justify-end">
                    <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => setEditingId(null)}>Cancel</Button>
                    <Button size="sm" className="h-6 text-xs" onClick={saveEdit} data-testid={`${entityType}-note-save`}>Save</Button>
                  </div>
                </div>
              ) : (
                <p className="text-sm whitespace-pre-wrap">{renderWithMentions(n.text)}</p>
              )}
              {n.attachments?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {n.attachments.map((a, i) => (
                    <button key={i} type="button" onClick={() => openAuthFile(a.url)} className="flex items-center gap-1 text-[10px] bg-muted px-2 py-1 rounded hover:bg-muted/80">
                      <FileIcon size={10} /> {a.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
